import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { api } from '@/api'
import type { LoginResponse } from '@/api/types'
import { signInErrorText } from '@/domain/errors'
import { inviteToken } from '@/domain/invites'
import { t } from '@/i18n'
import { Button } from '@/ui/button'
import { Field, PasswordField } from '@/ui/field'
import { InlineBanner } from '@/ui/inline-banner'
import { AuthFrame } from '@/ui/auth-frame'

/// `?invite=<token>`: signing in from an invitation goes back to it and
/// accepts it. A token, never a path — see `inviteToken`.
type SignInSearch = { invite?: string }

export const Route = createFileRoute('/sign-in')({
  validateSearch: (search: Record<string, unknown>): SignInSearch => {
    const invite = inviteToken(search['invite'])
    return invite ? { invite } : {}
  },
  beforeLoad: ({ search }) => {
    if (!api.hasSession()) return
    if (search.invite) throw redirect({ to: '/invite/$token', params: { token: search.invite } })
    throw redirect({ to: '/' })
  },
  component: SignIn,
})

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type Step = Readonly<{ kind: 'password' }> | Readonly<{ kind: 'code'; challengeToken: string }>

type Tokens = Readonly<{ accessToken: string; refreshToken: string }>

/// AuthScaffold of the iOS app on a desktop, in the `AuthFrame`: a title and
/// one quiet line, the fields, the server's refusal as an InlineBanner right
/// above the one amber button, and the way out as a footnote under a hairline
/// right below the form — where the eye already is.
function SignIn() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { invite } = Route.useSearch()
  const [step, setStep] = useState<Step>({ kind: 'password' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'email' | 'password' | 'code', string>>>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const finish = async (tokens: Tokens) => {
    api.setTokens(tokens)
    queryClient.clear()
    await (invite
      ? navigate({ to: '/invite/$token', params: { token: invite }, search: { accept: true } })
      : navigate({ to: '/' }))
  }

  const submitPassword = async () => {
    const errors = {
      ...(email.trim() === ''
        ? { email: t.signIn.errors.emptyEmail }
        : EMAIL.test(email.trim())
          ? {}
          : { email: t.signIn.errors.badEmail }),
      ...(password === '' ? { password: t.signIn.errors.emptyPassword } : {}),
    }
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return
    setBusy(true)
    setServerError(null)
    try {
      const res = await api.request<LoginResponse>('/auth/login', {
        body: { email: email.trim(), password },
        skipAuth: true,
      })
      if ('requires2fa' in res) {
        setStep({ kind: 'code', challengeToken: res.challengeToken })
        setCode('')
      } else {
        await finish(res)
      }
    } catch (error) {
      setServerError(signInErrorText(error, 'password'))
    } finally {
      setBusy(false)
    }
  }

  const submitCode = async (challengeToken: string) => {
    const digits = code.replace(/\s/g, '')
    if (!/^\d{6}$/.test(digits)) {
      setFieldErrors({ code: t.signIn.errors.badCode })
      return
    }
    setFieldErrors({})
    setBusy(true)
    setServerError(null)
    try {
      const res = await api.request<LoginResponse>('/auth/2fa/verify', {
        body: { challengeToken, code: digits },
        skipAuth: true,
      })
      if ('accessToken' in res) await finish(res)
    } catch (error) {
      const text = signInErrorText(error, 'code')
      if (text === t.signIn.errors.expiredChallenge) {
        // The challenge is single-use and short-lived: back to the password.
        setStep({ kind: 'password' })
        setPassword('')
      }
      setServerError(text)
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    void (step.kind === 'password' ? submitPassword() : submitCode(step.challengeToken))
  }

  return (
    <AuthFrame>
      <div className="mb-8 flex flex-col gap-2 text-center">
        <h1 className="text-title font-bold tracking-[-0.015em] text-balance text-text">
          {step.kind === 'password' ? t.signIn.title : t.signIn.codeTitle}
        </h1>
        <p className="text-callout text-pretty text-text-2">
          {step.kind === 'password' ? (invite ? t.signIn.inviteMessage : t.signIn.message) : t.signIn.codeMessage}
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {step.kind === 'password' ? (
          <>
            <Field
              label={t.signIn.email}
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoFocus
              placeholder={t.signIn.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={fieldErrors.email}
            />
            <PasswordField
              label={t.signIn.password}
              name="password"
              autoComplete="current-password"
              placeholder={t.signIn.passwordPlaceholder}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={fieldErrors.password}
            />
          </>
        ) : (
          <Field
            label={t.signIn.code}
            name="code"
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
            maxLength={7}
            placeholder="000000"
            className="[&_input]:text-center [&_input]:text-heading [&_input]:tracking-[0.3em] [&_input]:tabular"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^\d\s]/g, ''))}
            error={fieldErrors.code}
          />
        )}

        {serverError ? <InlineBanner kind="error" message={serverError} /> : null}

        <Button type="submit" variant="primary" busy={busy} className="mt-1 h-11 w-full sm:h-11">
          {step.kind === 'password'
            ? busy
              ? t.signIn.submitting
              : t.signIn.submit
            : busy
              ? t.signIn.codeSubmitting
              : t.signIn.codeSubmit}
        </Button>

        {step.kind === 'code' ? (
          <Button
            variant="quiet"
            className="self-center"
            onClick={() => {
              setStep({ kind: 'password' })
              setServerError(null)
              setPassword('')
            }}
          >
            {t.signIn.useAnotherAccount}
          </Button>
        ) : null}
      </form>

      <p className="mt-8 border-t border-surface-3 pt-6 text-center text-caption text-pretty text-text-2">
        {invite ? (
          <Link
            to="/invite/$token"
            params={{ token: invite }}
            className="underline decoration-border-strong underline-offset-4 hover:text-text hover:decoration-text"
          >
            {t.signIn.backToInvite}
          </Link>
        ) : (
          t.signIn.footnote
        )}
      </p>
    </AuthFrame>
  )
}
